import { useEffect, useState, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Outlet } from "react-router-dom";
import { getUsers } from "./services/newdaily";
import Main from "./views/Main/Main";
import Header from "./components/Header/Header";
import HeaderMobile from "./components/Header/HeaderMobile";
import Admin from "./views/Admin/AdminView";
import MainMobile from "./views/Main/MainMobile";
import AdminMobile from "./views/Admin/AdminMobile";
import ControlHorario from "./views/ControlHorario/ControlHorario";
import ControlHorarioMobile from "./views/ControlHorario/ControlHorarioMobile";
import Exits from "./views/Exits/Exits";
import ExitsMobile from "./views/Exits/ExitsMobile";
import { io } from "socket.io-client";
import { jwtVerify } from "jose";
import { SpinnerComponent } from "./components/Spinner";
import LegalPages from "./views/Legal";

const METROPOLIS_URL = "https://login.imetropolis.es/"

const NotFound = () => {
  return (
    <div style={{ textAlign: "center", padding: "2rem" }}>
      <h1>404 - Página no encontrada</h1>
      <p>La página a la que intentas acceder no existe.</p>
    </div>
  );
};

function ProtectedRoute({ isAdmin }) {
  if (!isAdmin) return <NotFound />;
  return <Outlet />;
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

async function importPublicKey(pem) {
  const b64 = pem.replace(/-----BEGIN PUBLIC KEY-----/, "")
    .replace(/-----END PUBLIC KEY-----/, "")
    .replace(/\s/g, "");
  const binaryDer = Uint8Array.from(window.atob(b64), (c) => c.charCodeAt(0));
  return await window.crypto.subtle.importKey(
    "spki",
    binaryDer.buffer,
    {
      name: "RSASSA-PKCS1-v1_5",
      hash: "SHA-256",
    },
    true,
    ["verify"]
  );
}

async function decodeAndVerifyToken(token) {
  const publicKeyPem = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAqMMdsHKTWf3mSp6C+zGc
6FgsIYj5TTVHNw95jFcrtOfKdmsF1uT0JtWf+MvyKkRMM8Odx/E5eEpsRHpKJKUZ
XZt4hEYy5EkvTSY9rUXS8welnuKeJ8OUrvb74kbGnLmwQNAGceoJ5msCYth6PmwT
oaQOeLSTPc1ciyfx+VPERV+zDpD5IYI8zmfshVsbY0pqNGjbspPYBB/Ltgyo7k/c
rao2MSg26BWmSNs8MWQZqGrsyCwrqPxv0N4Ho53gs3WsFRi7KWUlCoG5a4HptJR1
fvH4MYuR5pMn9DcdbPnlIxVwrt+97x1w+N9jdqUEjejfL9j9UdMmomNIUF9aYGoO
4wIDAQAB
-----END PUBLIC KEY-----`;
  try {
    const publicKey = await importPublicKey(publicKeyPem);
    const { payload } = await jwtVerify(token, publicKey, {
      algorithms: ["RS256"],
    });
    return payload;
  } catch (error) {
    console.error("Error al verificar el token:", error);
    return null;
  }
}

function AppWrapper() {
  const query = useQuery();

  const [empleado, setEmpleado] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1023);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1023);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

const redirectToLogin = () => {
  window.location.href = METROPOLIS_URL + "login/?URL=" + window.location.origin;
};

  useEffect(() => {
    const run = async () => {
      let token = query.get("TT");

      if (!token) {
        token = localStorage.getItem("TT");
      } else {
        localStorage.setItem("TT", token);
        const newUrl =
          window.location.protocol +
          "//" +
          window.location.host +
          window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      }

      if (query.get("Empleado") || !token) {
        redirectToLogin();
        return;
      }

      try {
        const payload = await decodeAndVerifyToken(token);

        if (!payload || !payload.userId) {
          redirectToLogin();
          return;
        }

        setEmpleado(payload.userId);
        setLoading(true);
        const data = await getUsers();

        if (!data?.Usuarios || !Array.isArray(data.Usuarios)) {
          redirectToLogin();
          return;
        }

        const user = data.Usuarios.find(
          (u) => u.id.toString() === payload.userId.toString()
        );

        if (!user) {
          redirectToLogin();
          return;
        }

        setIsAdmin(user.Admin === 1);
        setUserName(user.User);
      } catch (error) {
        console.error("Error al verificar empleado:", error);
        redirectToLogin();
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [query]);

  useEffect(() => {
    if (empleado) {
      localStorage.setItem("empleado", empleado);
    }
  }, [empleado]);

  useEffect(() => {
    if (!empleado) return;
    const socket = io("https://dl84.dinaserver.com", {
      transports: ["websocket"],
      secure: true
    });
    socket.on("databaseUpdate", (data) => {
      setRefreshSignal((prev) => prev + 1);
    });
    return () => {
      socket.disconnect();
    };
  }, [empleado]);

  if (loading) {
    return <SpinnerComponent avoidWait />;
  }

  if (!empleado) {
    return null;
  }

  return (
    <div className="appLayout">
      {isMobile ? (
        <HeaderMobile userName={userName} isAdmin={isAdmin} />
      ) : (
        <Header userName={userName} />
      )}

      <div className="appContent">
        <Routes>
          <Route
            path="/"
            element={
              isMobile ? (
                <MainMobile
                  empleado={empleado}
                  refreshSignal={refreshSignal}
                  isAdmin={isAdmin}
                  isMobile={isMobile}
                />
              ) : (
                <Main
                  empleado={empleado}
                  refreshSignal={refreshSignal}
                  isAdmin={isAdmin}
                />
              )
            }
          />

          <Route path="/admin" element={<ProtectedRoute isAdmin={isAdmin} />}>
            <Route
              index
              element={
                isMobile ? (
                  <AdminMobile
                    empleado={empleado}
                    refreshSignal={refreshSignal}
                    isMobile={isMobile}
                  />
                ) : (
                  <Admin empleado={empleado} refreshSignal={refreshSignal} />
                )
              }
            />
            <Route
              path="salidas"
              element={
                isMobile ? (
                  <ExitsMobile
                    empleado={empleado}
                    refreshSignal={refreshSignal}
                    isAdmin={isAdmin}
                    isMobile={isMobile}
                  />
                ) : (
                  <Exits
                    empleado={empleado}
                    refreshSignal={refreshSignal}
                    isAdmin={isAdmin}
                  />
                )
              }
            />
          </Route>

          <Route
            path="/control-horario"
            element={
              isMobile ? (
                <ControlHorarioMobile
                  empleado={empleado}
                  refreshSignal={refreshSignal}
                  isAdmin={isAdmin}
                  isMobile={isMobile}
                />
              ) : (
                <ControlHorario
                  empleado={empleado}
                  refreshSignal={refreshSignal}
                  isAdmin={isAdmin}
                />
              )
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </div>
  );

}

function App() {
  return (
    <Router>
      <Routes>
        {/*
          Aislado a proposito de AppWrapper: no pasa por el chequeo de
          token/login, para que la tienda (Apple/Google) y cualquier
          visitante sin sesion puedan verla siempre.
        */}
        <Route path="/legal/*" element={<LegalPages />} />
        <Route path="*" element={<AppWrapper />} />
      </Routes>
    </Router>
  );
}

export default App;
